import { 
  collection, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../../firebase';
import { getAccountById } from '../firestoreService';
import { triggerNewQuestionNotification } from '../notificationTriggerService';

const QUESTIONS_COLLECTION = 'questions';
const MESSAGES_SUBCOLLECTION = 'messages';

/**
 * Resolve a question's accountId to a company name.
 *
 * Questions submitted from the portal store `companyId || currentUser.uid` as the
 * accountId, so the id is often an auth uid rather than an accounts doc id. Try the
 * accounts collection first, then fall back to resolving the uid through the user's
 * doc (accountId/clientId) and finally through their email.
 */
const resolveCompanyName = async (accountId) => {
  if (!accountId) return null;

  const accountSnap = await getDoc(doc(db, 'accounts', accountId));
  if (accountSnap.exists()) {
    return accountSnap.data().company || null;
  }

  // Not an account id — treat it as a portal user's uid
  const userSnap = await getDoc(doc(db, 'users', accountId));
  if (!userSnap.exists()) return null;

  const userData = userSnap.data();
  const companyId = userData.accountId || userData.clientId || null;

  if (companyId) {
    const companySnap = await getDoc(doc(db, 'accounts', companyId));
    if (companySnap.exists()) {
      return companySnap.data().company || null;
    }
  }

  if (userData.email) {
    const byEmail = await getDocs(
      query(collection(db, 'accounts'), where('contact.email', '==', userData.email))
    );
    if (!byEmail.empty) {
      return byEmail.docs[0].data().company || null;
    }
  }

  return null;
};

// Helper function to enrich questions with account company names
const enrichQuestionsWithCompanyNames = async (questions) => {
  try {
    // Resolve each distinct accountId once rather than per question
    const accountIds = [...new Set(questions.map(q => q.accountId).filter(Boolean))];
    const companyNames = {};

    await Promise.all(accountIds.map(async (accountId) => {
      try {
        companyNames[accountId] = await resolveCompanyName(accountId);
      } catch (error) {
        console.warn(`Could not resolve company for account ${accountId}:`, error);
        companyNames[accountId] = null;
      }
    }));

    return questions.map(question => ({
      ...question,
      companyName: companyNames[question.accountId]
        || question.accountName
        || 'Unknown Company'
    }));
  } catch (error) {
    console.error('Error enriching questions with company names:', error);
    return questions;
  }
};

// Helper function to get user's company name
export const getUserCompanyName = async (userId) => {
  try {
    // First try to get the account directly using the user ID as account ID
    try {
      const account = await getAccountById(userId);
      return account.company || 'Unknown Company';
    } catch (error) {
      // If that fails, the user ID might not be the account ID
      // In that case, we'll need to look up the user's account ID first
      console.warn(`Could not get account directly for user ${userId}:`, error);
      return 'Unknown Company';
    }
  } catch (error) {
    console.error('Error getting user company name:', error);
    return 'Unknown Company';
  }
};

// Get all questions
export const getAllQuestions = async () => {
  try {
    const questionsRef = collection(db, QUESTIONS_COLLECTION);
    const q = query(questionsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const questions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
      resolvedAt: doc.data().resolvedAt?.toDate?.() || doc.data().resolvedAt
    }));
    
    return await enrichQuestionsWithCompanyNames(questions);
  } catch (error) {
    console.error('Error getting questions:', error);
    throw error;
  }
};

// Get questions by account
export const getQuestionsByAccount = async (accountId) => {
  try {
    const questionsRef = collection(db, QUESTIONS_COLLECTION);
    const q = query(
      questionsRef, 
      where('accountId', '==', accountId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const questions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
      resolvedAt: doc.data().resolvedAt?.toDate?.() || doc.data().resolvedAt
    }));
    
    return await enrichQuestionsWithCompanyNames(questions);
  } catch (error) {
    console.error('Error getting questions by account:', error);
    throw error;
  }
};

// Get questions by actual account ID (for CRM view)
export const getQuestionsByActualAccountId = async (accountId) => {
  try {
    // First, get all users associated with this account
    const usersRef = collection(db, 'users');
    const usersQuery = query(usersRef, where('accountId', '==', accountId));
    const usersSnapshot = await getDocs(usersQuery);
    
    const userIds = usersSnapshot.docs.map(doc => doc.id);
    
    if (userIds.length === 0) {
      return [];
    }
    
    // Query for questions where any of these users are the createdBy
    const questionsRef = collection(db, QUESTIONS_COLLECTION);
    const questionsPromises = userIds.map(userId => {
      const q = query(
        questionsRef,
        where('createdBy', '==', userId)
      );
      return getDocs(q);
    });
    
    const questionsSnapshots = await Promise.all(questionsPromises);
    
    // Combine and deduplicate questions
    const allQuestions = [];
    questionsSnapshots.forEach(snapshot => {
      const questions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
        resolvedAt: doc.data().resolvedAt?.toDate?.() || doc.data().resolvedAt
      }));
      allQuestions.push(...questions);
    });
    
    // Remove duplicates based on question ID
    const uniqueQuestions = allQuestions.filter((question, index, self) => 
      index === self.findIndex(q => q.id === question.id)
    );
    
    // Sort by creation date
    return uniqueQuestions.sort((a, b) => {
      if (!a.createdAt && !b.createdAt) return 0;
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return b.createdAt - a.createdAt; // Descending order
    });
    
  } catch (error) {
    console.error('Error getting questions by actual account ID:', error);
    throw error;
  }
};

// Get questions by assignee
export const getQuestionsByAssignee = async (assigneeId) => {
  try {
    const questionsRef = collection(db, QUESTIONS_COLLECTION);
    const q = query(
      questionsRef, 
      where('assigneeId', '==', assigneeId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const questions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
      resolvedAt: doc.data().resolvedAt?.toDate?.() || doc.data().resolvedAt
    }));
    
    return await enrichQuestionsWithCompanyNames(questions);
  } catch (error) {
    console.error('Error getting questions by assignee:', error);
    throw error;
  }
};

// Get open questions by assignee
export const getOpenQuestionsByAssignee = async (assigneeId) => {
  try {
    const questionsRef = collection(db, QUESTIONS_COLLECTION);
    const q = query(
      questionsRef, 
      where('assigneeId', '==', assigneeId),
      where('status', '==', 'Open')
    );
    const querySnapshot = await getDocs(q);
    const questions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
      resolvedAt: doc.data().resolvedAt?.toDate?.() || doc.data().resolvedAt
    }));
    
    // Sort in memory instead of in the query
    const sortedQuestions = questions.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
    
    return await enrichQuestionsWithCompanyNames(sortedQuestions);
  } catch (error) {
    console.error('Error getting open questions by assignee:', error);
    throw error;
  }
};

// Get questions by status
export const getQuestionsByStatus = async (status) => {
  try {
    const questionsRef = collection(db, QUESTIONS_COLLECTION);
    const q = query(
      questionsRef, 
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const questions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
      resolvedAt: doc.data().resolvedAt?.toDate?.() || doc.data().resolvedAt
    }));
    
    return await enrichQuestionsWithCompanyNames(questions);
  } catch (error) {
    console.error('Error getting questions by status:', error);
    throw error;
  }
};

// Get a single question by ID
export const getQuestionById = async (questionId) => {
  try {
    const questionRef = doc(db, QUESTIONS_COLLECTION, questionId);
    const questionDoc = await getDoc(questionRef);
    
    if (!questionDoc.exists()) {
      throw new Error('Question not found');
    }
    
    const question = {
      id: questionDoc.id,
      ...questionDoc.data(),
      createdAt: questionDoc.data().createdAt?.toDate?.() || questionDoc.data().createdAt,
      updatedAt: questionDoc.data().updatedAt?.toDate?.() || questionDoc.data().updatedAt,
      resolvedAt: questionDoc.data().resolvedAt?.toDate?.() || questionDoc.data().resolvedAt
    };
    
    // Enrich with company name
    const enrichedQuestions = await enrichQuestionsWithCompanyNames([question]);
    return enrichedQuestions[0];
  } catch (error) {
    console.error('Error getting question:', error);
    throw error;
  }
};

// Create a new question
export const createQuestion = async (questionData) => {
  try {
    const questionsRef = collection(db, QUESTIONS_COLLECTION);
    const newQuestion = {
      ...questionData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'Open'
    };
    
    const docRef = await addDoc(questionsRef, newQuestion);
    
    console.log('✅ Question created successfully:', docRef.id);
    
    // Trigger new question notification
    try {
      await triggerNewQuestionNotification({
        questioner: questionData.createdByName || questionData.accountName || 'Unknown',
        question: questionData.questionText,
        category: questionData.category || 'General',
        priority: questionData.priority || 'medium',
        assigneeId: questionData.assigneeId,
        assigneeName: questionData.assigneeName,
        accountId: questionData.accountId,
        accountName: questionData.accountName,
        timestamp: new Date().toISOString()
      });
      console.log('✅ New question notification sent successfully');
    } catch (notificationError) {
      console.error('❌ Error sending new question notification:', notificationError);
      // Don't fail the question creation if notification fails
    }
    
    return {
      id: docRef.id,
      ...newQuestion
    };
  } catch (error) {
    console.error('Error creating question:', error);
    throw error;
  }
};

// Update a question
export const updateQuestion = async (questionId, updateData) => {
  try {
    const questionRef = doc(db, QUESTIONS_COLLECTION, questionId);
    const update = {
      ...updateData,
      updatedAt: serverTimestamp()
    };
    
    // If status is being set to 'Resolved', set resolvedAt
    if (updateData.status === 'Resolved' && !updateData.resolvedAt) {
      update.resolvedAt = serverTimestamp();
    }
    
    await updateDoc(questionRef, update);
    return { id: questionId, ...update };
  } catch (error) {
    console.error('Error updating question:', error);
    throw error;
  }
};

// Get messages for a question
export const getQuestionMessages = async (questionId) => {
  try {
    const messagesRef = collection(db, QUESTIONS_COLLECTION, questionId, MESSAGES_SUBCOLLECTION);
    const q = query(messagesRef, orderBy('timestamp', 'asc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate?.() || doc.data().timestamp
    }));
  } catch (error) {
    console.error('Error getting question messages:', error);
    throw error;
  }
};

// Add a message to a question
export const addQuestionMessage = async (questionId, messageData) => {
  try {
    const messagesRef = collection(db, QUESTIONS_COLLECTION, questionId, MESSAGES_SUBCOLLECTION);
    const newMessage = {
      ...messageData,
      timestamp: serverTimestamp()
    };
    
    const docRef = await addDoc(messagesRef, newMessage);
    return {
      id: docRef.id,
      ...newMessage
    };
  } catch (error) {
    console.error('Error adding message:', error);
    throw error;
  }
};

// Real-time listener for questions. Portal callers pass their accountId: clients may
// only read their own company's questions (firestore.rules), so the filter is in the query.
export const subscribeToQuestions = (callback, accountId = null) => {
  const questionsRef = collection(db, QUESTIONS_COLLECTION);
  const q = accountId
    ? query(questionsRef, where('accountId', '==', accountId), orderBy('createdAt', 'desc'))
    : query(questionsRef, orderBy('createdAt', 'desc'));
  
  return onSnapshot(q, async (querySnapshot) => {
    const questions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
      resolvedAt: doc.data().resolvedAt?.toDate?.() || doc.data().resolvedAt
    }));
    
    // Enrich with company names
    const enrichedQuestions = await enrichQuestionsWithCompanyNames(questions);
    callback(enrichedQuestions);
  });
};

// Real-time listener for question messages
export const subscribeToQuestionMessages = (questionId, callback) => {
  const messagesRef = collection(db, QUESTIONS_COLLECTION, questionId, MESSAGES_SUBCOLLECTION);
  const q = query(messagesRef, orderBy('timestamp', 'asc'));
  
  return onSnapshot(q, (querySnapshot) => {
    const messages = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate?.() || doc.data().timestamp
    }));
    callback(messages);
  });
};

// Delete a question and all its messages
export const deleteQuestion = async (questionId) => {
  try {
    // First, delete all messages for this question
    const messagesRef = collection(db, QUESTIONS_COLLECTION, questionId, MESSAGES_SUBCOLLECTION);
    const messagesSnapshot = await getDocs(messagesRef);
    
    const deleteMessagePromises = messagesSnapshot.docs.map(messageDoc => 
      deleteDoc(doc(db, QUESTIONS_COLLECTION, questionId, MESSAGES_SUBCOLLECTION, messageDoc.id))
    );
    await Promise.all(deleteMessagePromises);

    // Then delete the question itself
    const questionRef = doc(db, QUESTIONS_COLLECTION, questionId);
    await deleteDoc(questionRef);
    
    return questionId;
  } catch (error) {
    console.error('Error deleting question:', error);
    throw error;
  }
}; 